import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Users2 } from "lucide-react";
import type { BuyerTeamBoardDto, BuyerTeamBoardSpecialistDto } from "@/api/buyerReports";
import { SpecialistQuickPick } from "@/components/buyers/SpecialistQuickPick";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  RECORD_CARD_MIN_WIDTH,
  ResponsiveGrid,
  Timestamp,
} from "@/design-system/components/ui";
import { buyerKeys } from "@/hooks/buyers/buyerKeys";
import { buyerReportKeys } from "@/hooks/buyers/buyerReportKeys";
import { Measure } from "../indexTiles/MeasureTile";
import { listHref } from "./reportLinks";

/**
 * The team board (D11 / D12): one card per specialist, the stale leads beneath
 * each with a reassignment control where the caller is allowed one.
 *
 * <p>Several peers side by side, so each is a `Card` (§16.1); the grid reflows
 * on its CONTAINER through `ResponsiveGrid` (§12), never on the viewport. The
 * card minimum is `RECORD_CARD_MIN_WIDTH` — a measure row and a stale row with
 * a select both have to fit inside the card's own padding.</p>
 *
 * <p>A reassignment moves a lead from one card to another AND changes the
 * buyer's owner everywhere the list renders it, so a successful move
 * invalidates both the board prefix and the buyer prefix. The quick-pick's own
 * hook already refreshes the queue and the standings.</p>
 */
export function TeamBoard({ board }: { board: BuyerTeamBoardDto }) {
  const queryClient = useQueryClient();

  const onChanged = () => {
    void queryClient.invalidateQueries({ queryKey: buyerReportKeys.all });
    void queryClient.invalidateQueries({ queryKey: buyerKeys.all });
  };

  if (board.specialists.length === 0) {
    return (
      <EmptyState
        icon={Users2}
        title="No consultants on this team yet"
        description="Add consultants on the team's Members tab and their cards appear here."
        action={{ label: "Open the team", href: `/buyers/teams/${board.teamId}` }}
      />
    );
  }

  return (
    <div data-testid="team-board">
      <p className="text-caption text-gray-mid mb-space-3">
        Stale means no contact in {board.staleContactDays} days. Held and entitlement follow the
        person; every other figure counts this team&apos;s buyers only.
      </p>
      <ResponsiveGrid cols={3} minColWidth={RECORD_CARD_MIN_WIDTH} gap="md">
        {board.specialists.map((s) => (
          <SpecialistCard key={s.userId} specialist={s} teamId={board.teamId} onChanged={onChanged} />
        ))}
      </ResponsiveGrid>
    </div>
  );
}

function SpecialistCard({
  specialist: s,
  teamId,
  onChanged,
}: {
  specialist: BuyerTeamBoardSpecialistDto;
  teamId: string;
  onChanged: () => void;
}) {
  return (
    <Card className="min-w-0" data-testid={`board-card-${s.userId}`}>
      <CardHeader>
        <CardTitle className="text-lead">{s.displayName}</CardTitle>
        <Link
          to={listHref("/buyers", { team: teamId, scope: "all" })}
          className="text-small text-gold-shadow hover:underline"
        >
          View buyers
        </Link>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-x-space-5 gap-y-space-2">
          <Measure label="Held" value={`${s.held} / ${s.entitlement}`} detail="held / entitlement" />
          <Measure label="Overdue" value={s.overdueFollowUps} detail="follow-ups past due" />
          <Measure label="Stale" value={s.staleBuyers} detail="no recent contact" />
          <Measure label="New this week" value={s.newThisWeek} />
          <Measure label="Qualified" value={s.qualifiedThisMonth} detail="this month" />
          <Measure label="Converted" value={s.convertedThisMonth} detail="this month" />
        </div>

        {s.staleRows.length > 0 && (
          <div className="mt-space-4">
            <h4 className="font-display text-label font-semibold uppercase tracking-label text-gray-mid m-0 mb-space-2">
              Stale buyers
            </h4>
            <ul className="m-0 p-0 list-none flex flex-col gap-space-2">
              {s.staleRows.map((row) => (
                <li
                  key={row.buyerLeadId}
                  className="flex flex-wrap items-center justify-between gap-space-2 min-w-0"
                  data-testid={`stale-row-${row.buyerLeadId}`}
                >
                  <div className="min-w-0">
                    <Link
                      to={`/buyers/${row.buyerLeadId}`}
                      className="text-small text-onyx hover:underline"
                    >
                      {row.staffDisplayName ?? row.displayName}
                    </Link>
                    <div className="text-caption text-gray-mid">
                      {row.lastContactAtUtc ? (
                        <Timestamp value={row.lastContactAtUtc} prefix="Last contact" />
                      ) : (
                        "Never contacted"
                      )}
                    </div>
                  </div>
                  <SpecialistQuickPick
                    buyerLeadId={row.buyerLeadId}
                    currentSpecialistUserId={s.userId}
                    currentSpecialistName={s.displayName}
                    canReassign={row.canReassign}
                    canUnassign={row.canUnassign ?? false}
                    onChanged={onChanged}
                    size="sm"
                    buyerDisplayName={row.displayName}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
